import {
  BotPromptChannel,
  BotSequenceActiveSuppressionsResponse,
  BotSequenceActiveSuppressionsSortKey,
  BotSequenceEventStats,
  BotSequenceEventsResponse,
  BotSequenceEventsSortKey,
  BotSequenceEventsSortOrder,
  BotSequenceFireMessageRequest,
  BotSequenceFireMessageResult,
  BotSequenceScheduledAttemptStatus,
  BotSequenceSuppressedEventStats,
  BotSequenceSuppressedEventsResponse,
  BotSequenceSuppressedOutcome,
  BotSequenceType,
  HourOfDay,
  OrgInterface,
  SequenceInterface,
  SequenceRecordInterface,
  SequenceSubType,
  SequenceType,
  SequenceUserEventInterface
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK apps/server/src/API/Sequences/Sequences.Controller.ts
//LINK apps/bot/src/API/Sequences/Sequences.Controller.ts

export interface TriggerSequenceResult {
  conversationId: number;
}

export class SequencesAPI {
  /**
   * Trigger an outbound sequence opening turn for a user.
   *
   * Closes any existing ACTIVE conversation in the same scope (org/user/channel/isTest),
   * creates a fresh conversation, and persists the bot's opening turn from the sequence prompt.
   * Returns the new conversation id so the chat client can connect to it.
   *
   * Sandbox (isTest=true) and production scopes are isolated.
   */
  static async triggerOpening(
    orgId: number,
    userId: number,
    sequenceSlug: string,
    channel: BotPromptChannel,
    isTest: boolean
  ): Promise<TriggerSequenceResult> {
    return APIService.Bot.post<TriggerSequenceResult>("/sequences/trigger", {
      orgId,
      userId,
      sequenceSlug,
      channel,
      isTest
    });
  }

  /**
   * Fire a sequence message end-to-end (ENG-2958): the bot generates the opening turn
   * from just-refreshed account data AND sends it as an SMS. Business outcomes
   * (`opted_out` / `sandboxed` / `skipped` / `error`) ride the 200 body; only
   * auth/infra failures throw. Called by the new engine's fire-pipeline handler —
   * pass exactly this shape (the bot's TSOA validation rejects extra properties).
   */
  static async fireSequenceMessage(params: BotSequenceFireMessageRequest): Promise<BotSequenceFireMessageResult> {
    return APIService.Bot.post<BotSequenceFireMessageResult>("/sequences/fire", params);
  }

  static forceCheckSequence(orgId: number, sequenceId: number, hour: HourOfDay) {
    return APIService.Bull.post<boolean>(`/sequences/org/${orgId}/sequence/${sequenceId}/check?hour=${hour}&force=true`);
  }

  static async checkAllSequencesForOrg(orgId: number, hour: HourOfDay) {
    return APIService.Bull.post(`/sequences/org/${orgId}/check?hour=${hour}`);
  }

  static async getOrgsWithSequencesEnabled() {
    return APIService.Server.get<OrgInterface[]>("/sequences/orgs/enabled");
  }

  static async getSequences(orgId: number) {
    return APIService.Server.get<SequenceInterface[]>(`/sequences/org/${orgId}`);
  }

  static async getSequenceRecords(orgId: number, sequenceId: number) {
    return APIService.Server.get<SequenceRecordInterface[]>(`/sequences/org/${orgId}/sequence/${sequenceId}/records`);
  }

  static async getSequenceUserEventsForRecord(orgId: number, sequenceId: number, recordId: number) {
    return APIService.Server.get<SequenceUserEventInterface[]>(`/sequences/org/${orgId}/sequence/${sequenceId}/record/${recordId}/events`);
  }

  static async createSequence(orgId: number, name: string, type: SequenceType, subType: SequenceSubType, questSlug: string) {
    return APIService.Server.post<SequenceInterface>(`/sequences/org/${orgId}`, { name, type, subType, questSlug });
  }

  static async updateSequence(orgId: number, sequenceId: number, sequence: Partial<SequenceInterface>) {
    return APIService.Server.put<SequenceInterface>(`/sequences/org/${orgId}/sequence/${sequenceId}`, sequence);
  }

  // ─── Sequence suppressions (ENG-2862 step 2, CRM page) ────────────────────
  // LINK apps/server/src/API/CRM/SequenceSuppressions/BotSequenceSuppressedEvents.Controller.ts

  /** The Sequences engine's not-sent decision feed, org-scoped. */
  static async getSuppressedEvents(orgId: number, params: BotSequenceSuppressedEventsParams) {
    return APIService.Server.get<BotSequenceSuppressedEventsResponse>(`/crm/sequence-suppressions/org/${orgId}${suppressionsQuery(params)}`);
  }

  /**
   * Segment badges (all types) + tiles and reason breakdown (the selected type). Tiles
   * and reasons follow the outcome/reason drill; the badges ignore it.
   */
  static async getSuppressedEventStats(orgId: number, params: Omit<BotSequenceSuppressedEventsParams, "page" | "pageSize">) {
    return APIService.Server.get<BotSequenceSuppressedEventStats>(`/crm/sequence-suppressions/org/${orgId}/stats${suppressionsQuery(params)}`);
  }

  /** Enrollments on hold right now — the Active suppressions tab. */
  static async getActiveSuppressions(orgId: number, params: BotSequenceActiveSuppressionsParams) {
    return APIService.Server.get<BotSequenceActiveSuppressionsResponse>(
      `/crm/sequence-suppressions/org/${orgId}/active-suppressions${suppressionsQuery(params)}`
    );
  }

  // ─── Sequence events (ENG-2990, CRM Sequences tab) ────────────────────────
  // LINK apps/server/src/API/CRM/SequenceEvents/BotSequenceEvents.Controller.ts

  /** The Sequences engine's general event feed (any status), org-scoped. */
  static async getSequenceEvents(orgId: number, params: BotSequenceEventsParams) {
    return APIService.Server.get<BotSequenceEventsResponse>(`/crm/sequence-events/org/${orgId}${suppressionsQuery(params)}`);
  }

  /** Segment badges (all types) + per-status counts (the selected type). */
  static async getSequenceEventStats(
    orgId: number,
    params: Omit<BotSequenceEventsParams, "statuses" | "outcome" | "reason" | "sortBy" | "sortOrder" | "page" | "pageSize">
  ) {
    return APIService.Server.get<BotSequenceEventStats>(`/crm/sequence-events/org/${orgId}/stats${suppressionsQuery(params)}`);
  }
}

/** Filters shared by the suppressed-events feed and its stats (see the CRM page). */
export interface BotSequenceSuppressedEventsParams {
  sequenceType: BotSequenceType;
  from?: string;
  to?: string;
  outcome?: BotSequenceSuppressedOutcome;
  reason?: string;
  /** `"none"` = rows whose location belongs to no group (or that have no location at all). */
  groupId?: number | "none";
  /** `"none"` = rows with no location (the CRM sidebar's "No Location" entry). */
  locationId?: number | "none";
  /** Free-text: `123` / `#123` = exact internal user id, anything else = name / phone. */
  user?: string;
  page?: number;
  pageSize?: number;
}

/** The Active suppressions tab has no date or reason dimension — `suppressUntil > now()` is all of it. */
export type BotSequenceActiveSuppressionsParams = Omit<BotSequenceSuppressedEventsParams, "from" | "to" | "outcome" | "reason"> & {
  sortBy?: BotSequenceActiveSuppressionsSortKey;
  sortOrder?: BotSequenceEventsSortOrder;
};

/** Filters shared by the sequence-events feed and its stats (see the CRM Sequences page). */
export interface BotSequenceEventsParams {
  sequenceType: BotSequenceType;
  from?: string;
  to?: string;
  /** Narrows to the given ledger statuses; omitted/empty = all. */
  statuses?: BotSequenceScheduledAttemptStatus[];
  /** `"none"` = rows whose location belongs to no group (or that have no location at all). */
  groupId?: number | "none";
  /** `"none"` = rows with no location (the CRM sidebar's "No Location" entry). */
  locationId?: number | "none";
  /** Free-text: `123` / `#123` = exact internal user id, anything else = name / phone. */
  user?: string;
  /** The "By reason" drill (the suppressions page absorbed into Sequences): a family and, inside it, one reason. */
  outcome?: BotSequenceSuppressedOutcome;
  reason?: string;
  sortBy?: BotSequenceEventsSortKey;
  sortOrder?: BotSequenceEventsSortOrder;
  page?: number;
  pageSize?: number;
}

function suppressionsQuery(params: Partial<BotSequenceSuppressedEventsParams> | Partial<BotSequenceEventsParams>): string {
  const queryParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    if (Array.isArray(value)) {
      // TSOA array queries read repeated params (`statuses=a&statuses=b`), not a CSV.
      for (const item of value) {
        queryParams.append(key, String(item));
      }
      continue;
    }
    queryParams.append(key, String(value));
  }
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}
